import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowUpDown,
  Building2,
  ChevronDown,
  ChevronRight,
  FolderTree,
  Globe,
  MapPin,
  PieChart,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  WalletCards,
  Users,
  X,
} from "lucide-react";
import { showToastMessage } from "utils";
import AdminPassPreview from "../components/AdminPassPreview";
import StatusBadge from "../components/StatusBadge";
import EnterpriseAccountFormSheet from "./EnterpriseAccountFormSheet";
import useWorkspaceOverview from "../workspace/useWorkspaceOverview";
import {
  createEnterpriseAccountV2,
  generateWorkspaceCreatePasswordLinkV2,
  generateWorkspaceWalletLinkV2,
  getEnterpriseAccountDetailV2,
  getEnterpriseAccountsV2,
  resendWorkspaceCreatePasswordV2,
  resendWorkspaceWalletEmailV2,
  updateEnterpriseAccountV2,
} from "./api";

function formatDays(value) {
  if (value == null) return "-";
  if (value < 0) return `${Math.abs(value)} overdue`;
  if (value === 0) return "Today";
  return `${value} days`;
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
}

function formatTerm(value, health) {
  const days = Number(value || 0);
  if (health === "No subscription") return "No subscription";
  if (days >= 365) return "12 month term";
  if (days >= 60) return `${Math.round(days / 30)} month term`;
  if (days >= 30) return "1 month term";
  if (days > 0) return `${days} day term`;
  return "Custom term";
}

function getTermDisplay(row) {
  return {
    label: row?.termLabel || formatTerm(row?.packageDays || row?.subscriptionPeriod * 30, row?.health),
    hint: row?.termHint || "",
  };
}

function formatUrlLabel(value) {
  if (!value) return "-";

  return String(value).replace(/^https?:\/\//, "");
}

function formatRoleLabel(value) {
  if (!value) return "Users";

  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function EnterpriseRowActions({ row, onEdit }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onEdit(row);
      }}
      className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-[var(--admin-v2-ink)] hover:tw-border-[rgba(31,41,64,0.22)]"
    >
      Edit
    </button>
  );
}

function WorkspaceEmailActions({
  userId,
  userName,
  onCreatePassword,
  onWalletEmail,
  onCopyCreatePassword,
  onCopyWallet,
  createLoading = false,
  walletLoading = false,
  copyPasswordLoading = false,
  copyWalletLoading = false,
}) {
  const buttonClass =
    "tw-inline-flex tw-items-center tw-justify-center tw-rounded-2xl tw-border tw-px-3.5 tw-py-2.5 tw-text-xs tw-font-medium tw-transition disabled:tw-cursor-not-allowed disabled:tw-opacity-50";

  return (
    <div className="tw-mt-4 tw-rounded-[24px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[linear-gradient(180deg,rgba(248,250,255,0.96),rgba(255,255,255,0.98))] tw-p-4">
      <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
        <div className="tw-space-y-3">
          <div>
            <p className="admin-v2-eyebrow">Email actions</p>
            <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
              Send Cardly emails directly to {userName || "this user"}.
            </p>
          </div>
          <div className="tw-grid tw-gap-2">
            <button
              type="button"
              disabled={!userId || createLoading || walletLoading || copyPasswordLoading || copyWalletLoading}
              onClick={(event) => {
                event.stopPropagation();
                onCreatePassword(userId);
              }}
              className={`${buttonClass} tw-border-[rgba(243,86,88,0.18)] tw-bg-[rgba(243,86,88,0.08)] tw-text-[#c44749]`}
            >
              {createLoading ? "Sending..." : "Send create password email"}
            </button>
            <button
              type="button"
              disabled={!userId || createLoading || walletLoading || copyPasswordLoading || copyWalletLoading}
              onClick={(event) => {
                event.stopPropagation();
                onWalletEmail(userId);
              }}
              className={`${buttonClass} tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-text-[var(--admin-v2-ink)]`}
            >
              {walletLoading ? "Sending..." : "Send add to wallet email"}
            </button>
          </div>
        </div>

        <div className="tw-space-y-3">
          <div>
            <p className="admin-v2-eyebrow">Direct links</p>
            <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
              Copy a ready link if the user cannot access email right now.
            </p>
          </div>
          <div className="tw-grid tw-gap-2">
            <button
              type="button"
              disabled={!userId || createLoading || walletLoading || copyPasswordLoading || copyWalletLoading}
              onClick={(event) => {
                event.stopPropagation();
                onCopyCreatePassword(userId, userName);
              }}
              className={`${buttonClass} tw-border-[rgba(44,50,85,0.12)] tw-bg-[rgba(44,50,85,0.05)] tw-text-[var(--admin-v2-ink)]`}
            >
              {copyPasswordLoading ? "Copying..." : "Copy create password link"}
            </button>
            <button
              type="button"
              disabled={!userId || createLoading || walletLoading || copyPasswordLoading || copyWalletLoading}
              onClick={(event) => {
                event.stopPropagation();
                onCopyWallet(userId, userName);
              }}
              className={`${buttonClass} tw-border-[rgba(44,50,85,0.12)] tw-bg-[rgba(44,50,85,0.05)] tw-text-[var(--admin-v2-ink)]`}
            >
              {copyWalletLoading ? "Copying..." : "Copy add to wallet link"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function compareValues(left, right) {
  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return String(left || "").localeCompare(String(right || ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function MetricTile({ label, value, note, tone = "default" }) {
  const toneClass =
    tone === "accent"
      ? "tw-bg-[rgba(243,86,88,0.08)] tw-border-[rgba(243,86,88,0.12)]"
      : tone === "warm"
      ? "tw-bg-[rgba(250,216,69,0.16)] tw-border-[rgba(250,216,69,0.2)]"
      : "tw-bg-[rgba(44,50,85,0.04)] tw-border-[rgba(31,41,64,0.08)]";

  return (
    <div className={`tw-rounded-[20px] tw-border tw-p-4 ${toneClass}`}>
      <p className="admin-v2-eyebrow">{label}</p>
      <p className="tw-mt-3 tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-[var(--admin-v2-ink)]">
        {value}
      </p>
      {note ? <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">{note}</p> : null}
    </div>
  );
}

function SectionToggle({ title, icon, open, onToggle, children, right }) {
  return (
    <section className="tw-rounded-[24px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4 sm:tw-p-5">
      <button
        type="button"
        onClick={onToggle}
        className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-text-left"
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          {icon}
          <p className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">{title}</p>
        </div>
        <div className="tw-flex tw-items-center tw-gap-3">
          {right}
          <span
            className={`tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-[rgba(44,50,85,0.06)] tw-text-[var(--admin-v2-ink)] tw-transition ${
              open ? "tw-rotate-180" : ""
            }`}
          >
            <ChevronDown size={16} />
          </span>
        </div>
      </button>
      {open ? <div className="tw-mt-4">{children}</div> : null}
    </section>
  );
}

function EnterpriseDetailPanel({
  detail,
  open,
  loading,
  onClose,
  selectedUserId,
  onSelectUser,
  onEditEnterprise,
  onCreatePassword,
  onWalletEmail,
  onCopyCreatePassword,
  onCopyWallet,
  isMailing,
}) {
  const [userQuery, setUserQuery] = useState("");
  const [showSignals, setShowSignals] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showWallets, setShowWallets] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const selectedUser =
    detail?.users?.find((user) => user.id === selectedUserId) || detail?.users?.[0] || null;
  const filteredTree = useMemo(() => {
    const normalizedQuery = userQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return detail?.tree || [];
    }

    return (detail?.tree || [])
      .map((group) => ({
        ...group,
        users: group.users.filter((user) =>
          [user.name, user.email, user.role]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(normalizedQuery))
        ),
      }))
      .filter((group) => group.users.length);
  }, [detail?.tree, userQuery]);

  useEffect(() => {
    const nextGroups = {};
    (detail?.tree || []).forEach((group) => {
      nextGroups[group.id] = false;
    });
    setOpenGroups(nextGroups);
    setUserQuery("");
    setShowSignals(false);
    setShowReports(false);
    setShowWallets(false);
    setShowUsers(false);
    setShowProfile(false);
  }, [detail]);

  if (!open) return null;

  return createPortal(
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-justify-end">
      <button
        type="button"
        aria-label="Close enterprise detail"
        className="tw-absolute tw-inset-0 tw-bg-[rgba(20,26,44,0.72)]"
        onClick={onClose}
      />
      <aside
        className="tw-relative tw-z-[1] tw-h-[calc(100vh+20px)] tw-w-full tw-max-w-[920px] tw-overflow-y-auto tw-border-l tw-border-[rgba(31,41,64,0.08)] tw-bg-[linear-gradient(180deg,#ffffff_0%,#f8faff_100%)] tw-p-0 tw-shadow-[0_0_0_1px_rgba(31,41,64,0.02)]"
        style={{
          borderTopLeftRadius: 28,
          borderBottomLeftRadius: 28,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        <div className="tw-sticky tw-top-0 tw-z-[2] tw-mb-5 tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-px-4 tw-py-5 sm:tw-px-5 lg:tw-px-6">
          <div>
            <p className="admin-v2-eyebrow">Enterprise detail</p>
            <h2 className="tw-mt-2 tw-text-xl tw-font-semibold tw-text-[var(--admin-v2-ink)] sm:tw-text-3xl">
              {detail?.overview?.organizationName || "Loading workspace"}
            </h2>
            <div className="tw-mt-3 tw-inline-flex tw-items-center tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[rgba(248,250,255,0.96)] tw-px-4 tw-py-2.5">
              <p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-muted)]">
                Package health, team members, and workspace signals.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-text-[var(--admin-v2-ink)] hover:tw-border-[rgba(31,41,64,0.16)]"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="tw-px-4 tw-pb-4 sm:tw-px-5 lg:tw-px-6 admin-v2-text-muted">Loading enterprise detail...</div>
        ) : !detail ? (
          <div className="tw-space-y-3 tw-px-4 tw-pb-4 sm:tw-px-5 lg:tw-px-6">
            <h3 className="tw-text-lg tw-font-semibold tw-text-[var(--admin-v2-ink)]">
              Select an enterprise
            </h3>
            <p className="admin-v2-text-muted">
              Click any row to inspect renewal, package, usage, and the full user tree for that workspace.
            </p>
          </div>
        ) : (
          <div className="tw-space-y-5 tw-px-4 tw-pb-4 sm:tw-px-5 lg:tw-px-6 lg:tw-pb-6">
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
            <div>
              <p className="admin-v2-eyebrow">Workspace overview</p>
              <h3 className="tw-mt-2 tw-text-2xl tw-font-semibold tw-tracking-tight tw-text-[var(--admin-v2-ink)]">
                {detail.overview.organizationName}
              </h3>
              <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
                {detail.overview.ownerName} | {detail.overview.packageName}
              </p>
            </div>
            <StatusBadge status={detail.overview.health} />
          </div>

          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
            <div className="tw-rounded-[20px] tw-bg-[rgba(44,50,85,0.04)] tw-p-4">
              <p className="admin-v2-eyebrow tw-mb-2">Renewal</p>
              <p className="tw-text-lg tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                {formatDays(detail.overview.daysLeft)}
              </p>
              <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
                {formatDate(detail.overview.nextPaymentDate)}
              </p>
            </div>
            <div className="tw-rounded-[20px] tw-bg-[rgba(250,216,69,0.14)] tw-p-4">
              <p className="admin-v2-eyebrow tw-mb-2">Capacity</p>
              <p className="tw-text-lg tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                {detail.overview.remainingCards} left
              </p>
              <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
                {detail.overview.usedCards} used / {detail.overview.totalCards} total
              </p>
            </div>
          </div>

          <div className="tw-grid tw-gap-3 sm:tw-grid-cols-3">
            <div className="tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4">
              <p className="admin-v2-eyebrow tw-mb-1">Users</p>
              <p className="tw-text-xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                {detail.overview.activeUsers}
              </p>
            </div>
            <div className="tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4">
              <p className="admin-v2-eyebrow tw-mb-1">Reports</p>
              <p className="tw-text-xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                {detail.overview.reportsCount}
              </p>
            </div>
            <div className="tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-p-4">
              <p className="admin-v2-eyebrow tw-mb-1">Designs</p>
              <p className="tw-text-xl tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                {detail.overview.designsCount}
              </p>
            </div>
          </div>

          <SectionToggle
            title="Workspace signals"
            icon={<Globe size={16} className="tw-text-[var(--admin-v2-accent)]" />}
            open={showSignals}
            onToggle={() => setShowSignals((value) => !value)}
          >
            <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2">
              <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                <p className="admin-v2-eyebrow tw-mb-1">Company URL</p>
                <p className="tw-break-all tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                  {formatUrlLabel(detail.overview.companyURL)}
                </p>
              </div>
              <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                <p className="admin-v2-eyebrow tw-mb-1">LinkedIn</p>
                <p className="tw-break-all tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                  {formatUrlLabel(detail.overview.linkedInURL)}
                </p>
              </div>
              <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                <p className="admin-v2-eyebrow tw-mb-1">Declared employees</p>
                <p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                  {detail.overview.numberOfEmployees || 0}
                </p>
              </div>
              <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                <p className="admin-v2-eyebrow tw-mb-1">Active cardholders</p>
                <p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                  {detail.overview.activeCardholders || 0}
                </p>
              </div>
            </div>
          </SectionToggle>

          <div className="tw-space-y-4">
            <SectionToggle
              title="Reports and roles"
              icon={<PieChart size={16} className="tw-text-[var(--admin-v2-accent)]" />}
              open={showReports}
              onToggle={() => setShowReports((value) => !value)}
            >

              <div className="tw-mt-4 tw-grid tw-gap-3 sm:tw-grid-cols-2">
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                  <p className="admin-v2-eyebrow tw-mb-1">Reports total</p>
                  <p className="tw-text-lg tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                    {detail.analytics?.reports?.total || 0}
                  </p>
                </div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                  <p className="admin-v2-eyebrow tw-mb-1">Reports (30d)</p>
                  <p className="tw-text-lg tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                    {detail.analytics?.reports?.last30Days || 0}
                  </p>
                </div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                  <p className="admin-v2-eyebrow tw-mb-1">Latest report</p>
                  <p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                    {formatDate(detail.analytics?.reports?.latestAt)}
                  </p>
                </div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                  <p className="admin-v2-eyebrow tw-mb-1">First report</p>
                  <p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                    {formatDate(detail.analytics?.reports?.firstAt)}
                  </p>
                </div>
              </div>

              <div className="tw-mt-4 tw-space-y-2">
                {(detail.analytics?.roles || []).map((item) => (
                  <div
                    key={item.role}
                    className="tw-flex tw-items-center tw-justify-between tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-2.5"
                  >
                    <span className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                      {formatRoleLabel(item.label)}
                    </span>
                    <span className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            </SectionToggle>

            <SectionToggle
              title="Wallets and locations"
              icon={<WalletCards size={16} className="tw-text-[var(--admin-v2-accent)]" />}
              open={showWallets}
              onToggle={() => setShowWallets((value) => !value)}
            >

              <div className="tw-mt-4 tw-grid tw-gap-3 sm:tw-grid-cols-2">
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                  <p className="admin-v2-eyebrow tw-mb-1">Apple devices</p>
                  <p className="tw-text-lg tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                    {detail.analytics?.wallets?.appleDevices || 0}
                  </p>
                </div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                  <p className="admin-v2-eyebrow tw-mb-1">Google devices</p>
                  <p className="tw-text-lg tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                    {detail.analytics?.wallets?.googleDevices || 0}
                  </p>
                </div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                  <p className="admin-v2-eyebrow tw-mb-1">Apple wallet users</p>
                  <p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                    {detail.analytics?.wallets?.appleWalletUsers || 0}
                  </p>
                </div>
                <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                  <p className="admin-v2-eyebrow tw-mb-1">Google wallet users</p>
                  <p className="tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                    {detail.analytics?.wallets?.googleWalletUsers || 0}
                  </p>
                </div>
              </div>

              <div className="tw-mt-4">
                <div className="tw-mb-2 tw-flex tw-items-center tw-gap-2">
                  <MapPin size={14} className="tw-text-[var(--admin-v2-accent)]" />
                  <p className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                    Top locations
                  </p>
                </div>
                <div className="tw-space-y-2">
                  {(detail.analytics?.locations || []).length ? (
                    detail.analytics.locations.map((item) => (
                      <div
                        key={item.label}
                        className="tw-flex tw-items-center tw-justify-between tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-2.5"
                      >
                        <span className="tw-truncate tw-pr-3 tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                          {item.label}
                        </span>
                        <span className="tw-text-sm tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                          {item.count}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="tw-rounded-2xl tw-bg-[rgba(44,50,85,0.04)] tw-px-3 tw-py-3 tw-text-sm admin-v2-text-muted">
                      No address data available for this workspace.
                    </div>
                  )}
                </div>
              </div>
            </SectionToggle>
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <StatusBadge status={detail.overview.webhookAccess ? "Webhook enabled" : "Webhook off"} />
            <StatusBadge status={detail.overview.emailAuth ? "Email auth" : "SSO only"} />
            {detail.overview.disabledUsers > 0 ? (
              <StatusBadge status={`${detail.overview.disabledUsers} disabled`} />
            ) : null}
          </div>

          <div className="tw-flex tw-justify-end">
            <EnterpriseRowActions row={detail.overview} onEdit={onEditEnterprise} />
          </div>

          <div className="tw-space-y-5">
            <SectionToggle
              title="User tree"
              icon={<FolderTree size={16} className="tw-text-[var(--admin-v2-accent)]" />}
              open={showUsers}
              onToggle={() => setShowUsers((value) => !value)}
              right={<span className="tw-text-xs tw-font-medium tw-text-[var(--admin-v2-muted)]">{detail.users?.length || 0} total</span>}
            >
              <div>
                <input
                  value={userQuery}
                  onChange={(event) => setUserQuery(event.target.value)}
                  placeholder="Search user by name, email, or role..."
                  className="tw-w-full tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)] focus:tw-border-[rgba(243,86,88,0.5)] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[rgba(243,86,88,0.08)]"
                />
              </div>

              <div className="tw-mt-4 tw-space-y-3 xl:tw-max-h-[64vh] xl:tw-overflow-y-auto xl:tw-pr-1">
                {filteredTree.map((group) => (
                  <div key={group.id} className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-3">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenGroups((current) => ({
                          ...current,
                          [group.id]: !current[group.id],
                        }))
                      }
                      className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-text-left"
                    >
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span
                          className={`tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-text-[var(--admin-v2-ink)] tw-transition ${
                            openGroups[group.id] ? "tw-rotate-180" : ""
                          }`}
                        >
                          <ChevronDown size={14} />
                        </span>
                        <p className="tw-text-sm tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-[var(--admin-v2-muted)]">
                          {formatRoleLabel(group.label)}
                        </p>
                      </div>
                      <span className="tw-text-xs tw-font-medium tw-text-[var(--admin-v2-ink)]">
                        {group.count} users
                      </span>
                    </button>

                    {openGroups[group.id] ? (
                      <div className="tw-mt-3 tw-space-y-2">
                        {group.users.map((user) => (
                          <div
                            key={user.id}
                            className={`tw-rounded-2xl tw-px-3 tw-py-3 tw-transition ${
                              selectedUser?.id === user.id
                                ? "tw-bg-white tw-shadow-[0_10px_20px_rgba(20,26,44,0.08)]"
                                : "hover:tw-bg-white/70"
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                onSelectUser(user.id);
                                setShowProfile(true);
                              }}
                              className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-text-left"
                            >
                              <div className="tw-min-w-0">
                                <p className="tw-truncate tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                                  {user.name}
                                </p>
                                <p className="tw-truncate tw-text-xs admin-v2-text-muted">
                                  {user.email}
                                </p>
                              </div>
                              <ChevronRight size={16} className="tw-text-[var(--admin-v2-muted)]" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
                {!filteredTree.length ? (
                  <div className="tw-rounded-[18px] tw-bg-[rgba(44,50,85,0.04)] tw-p-4 tw-text-sm admin-v2-text-muted">
                    No users match this search.
                  </div>
                ) : null}
              </div>
            </SectionToggle>

          {selectedUser && showProfile ? (
            <div className="tw-fixed tw-inset-0 tw-z-[3] tw-flex tw-items-center tw-justify-center tw-bg-[rgba(20,26,44,0.52)] tw-p-4">
              <div className="tw-w-full tw-max-w-[1060px] tw-rounded-[34px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)] tw-p-5 tw-shadow-[0_34px_90px_rgba(20,26,44,0.24)] sm:tw-p-6">
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-[rgba(31,41,64,0.08)] tw-pb-5">
                  <div className="tw-min-w-0">
                    <p className="admin-v2-eyebrow">User profile</p>
                    <p className="tw-mt-2 tw-text-[clamp(1.75rem,2.2vw,2.35rem)] tw-font-semibold tw-tracking-[-0.04em] tw-text-[var(--admin-v2-ink)]">
                      {selectedUser.name}
                    </p>
                    <p className="tw-mt-1 tw-text-[15px] tw-font-medium admin-v2-text-muted">
                      {selectedUser.jobTitle || "Workspace team member"}
                    </p>
                    <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2">
                      <StatusBadge status={formatRoleLabel(selectedUser.role)} />
                      <StatusBadge status={selectedUser.disabled ? "Disabled" : "Active"} />
                      <StatusBadge status={selectedUser.cardExist ? "Card active" : "No card"} />
                      <StatusBadge status={selectedUser.emailAuth ? "Email auth" : "Email auth off"} />
                      <StatusBadge
                        status={selectedUser.thirdPartyAuth ? "Third party auth" : "Local auth"}
                      />
                      {selectedUser.isOwner ? <StatusBadge status="Workspace owner" /> : null}
                    </div>
                    <WorkspaceEmailActions
                      userId={selectedUser.id}
                      userName={selectedUser.name}
                      onCreatePassword={onCreatePassword}
                      onWalletEmail={onWalletEmail}
                      onCopyCreatePassword={onCopyCreatePassword}
                      onCopyWallet={onCopyWallet}
                      createLoading={isMailing("password", selectedUser.id)}
                      walletLoading={isMailing("wallet", selectedUser.id)}
                      copyPasswordLoading={isMailing("copy-password", selectedUser.id)}
                      copyWalletLoading={isMailing("copy-wallet", selectedUser.id)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowProfile(false)}
                    className="tw-inline-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-text-[var(--admin-v2-ink)]"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="tw-mt-6 tw-grid tw-gap-6 lg:tw-grid-cols-[minmax(0,1.55fr)_360px]">
                  <div className="tw-space-y-4">
                    <div className="tw-rounded-[26px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[rgba(248,250,255,0.9)] tw-p-4 sm:tw-p-5">
                      <div className="tw-grid tw-gap-3">
                        <div className="tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-px-4 tw-py-3.5">
                          <p className="admin-v2-eyebrow">Email</p>
                          <p className="tw-mt-2 tw-break-all tw-text-[15px] tw-font-medium tw-leading-6 tw-text-[var(--admin-v2-ink)]">
                            {selectedUser.email || "-"}
                          </p>
                        </div>
                        <div className="tw-grid tw-gap-3 md:tw-grid-cols-2">
                          {[
                            { label: "Job title", value: selectedUser.jobTitle || "-" },
                            { label: "Phone", value: selectedUser.phone || "-" },
                            { label: "Created", value: formatDate(selectedUser.createdAt) },
                            { label: "Workspace role", value: formatRoleLabel(selectedUser.role) },
                          ].map((item) => (
                            <div
                              key={item.label}
                              className="tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-px-4 tw-py-3.5"
                            >
                              <p className="admin-v2-eyebrow">{item.label}</p>
                             <p
  className={`tw-mt-2 tw-text-[15px] tw-font-medium tw-leading-6 tw-text-[var(--admin-v2-ink)] ${
    item.label === "Job title" ? "tw-uppercase" : ""
  }`}
>
  {item.value}
</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="tw-grid tw-gap-3 sm:tw-grid-cols-3">
                      <MetricTile label="Email slots" value={selectedUser.emailsCount || 0} note="Connected inboxes" />
                      <MetricTile label="Phone slots" value={selectedUser.phonesCount || 0} note="Saved numbers" />
                      <MetricTile label="Addresses" value={selectedUser.addressesCount || 0} note="Mapped locations" />
                    </div>

                    {(selectedUser.addresses || []).length ? (
                      <div className="tw-rounded-[26px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[rgba(44,50,85,0.03)] tw-p-4 sm:tw-p-5">
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                          <div>
                            <p className="admin-v2-eyebrow">Address details</p>
                            <p className="tw-mt-1 tw-text-sm admin-v2-text-muted">
                              Saved locations for this user profile.
                            </p>
                          </div>
                          <span className="tw-rounded-full tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                            {(selectedUser.addresses || []).length} saved
                          </span>
                        </div>
                        <div className="tw-mt-4 tw-grid tw-gap-3">
                          {selectedUser.addresses.map((item, index) => (
                            <div
                              key={`${selectedUser.id}-address-${index}`}
                              className="tw-rounded-[18px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-white tw-px-4 tw-py-3.5 tw-text-[15px] tw-leading-6 tw-text-[var(--admin-v2-ink)]"
                            >
                              {[
                                item.addressLine1,
                                item.addressLine2,
                                item.city,
                                item.state,
                                item.country,
                                item.postalCode,
                              ]
                                .filter(Boolean)
                                .join(", ") || "Address record"}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="tw-flex tw-items-start tw-justify-center">
                    <div className="tw-w-full tw-max-w-[360px] tw-rounded-[28px] tw-border tw-border-[rgba(31,41,64,0.08)] tw-bg-[linear-gradient(180deg,rgba(44,50,85,0.04),rgba(44,50,85,0.01))] tw-p-4">
                      <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <div>
                          <p className="admin-v2-eyebrow">Saved card design</p>
                          <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-[var(--admin-v2-ink)]">
                            Original workspace card preview
                          </p>
                        </div>
                        <span className="tw-rounded-full tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                          Live
                        </span>
                      </div>
                      <div className="tw-flex tw-justify-center">
                        <AdminPassPreview preview={selectedUser.preview} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          </div>
        </div>
        )}
      </aside>
    </div>,
    document.body
  );
}

export default function EnterpriseAccountsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: workspaceData } = useWorkspaceOverview();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [mailingState, setMailingState] = useState({});
  const [query, setQuery] = useState("");
  const [healthFilter, setHealthFilter] = useState("all");
  const [sortKey, setSortKey] = useState("renewal");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState("");

  const loadRows = useCallback(async (mode = "initial") => {
    try {
      if (mode === "initial") {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const data = await getEnterpriseAccountsV2();
      setRows(data);
    } catch (error) {
      showToastMessage(error?.message || "Failed to load enterprise accounts");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (location.search === "?create=1") {
      setSelected(null);
      setSubmitError("");
      setSheetOpen(true);
    }
  }, [location.search]);

  const filteredRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesQuery =
        !normalizedQuery ||
        [row.organizationName, row.ownerName, row.email, row.packageName]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedQuery));

      const matchesHealth =
        healthFilter === "all" ||
        String(row.health || "").toLowerCase() === healthFilter;

      return matchesQuery && matchesHealth;
    });
  }, [healthFilter, query, rows]);

  const sortedRows = useMemo(() => {
    const getSortValue = (row) => {
      switch (sortKey) {
        case "workspace":
          return row.organizationName;
        case "owner":
          return row.ownerName || row.email;
        case "package":
          return `${row.packageName || ""} ${row.subscriptionPeriod || 0}`;
        case "cards":
          return Number(row.usedCards || 0);
        case "security":
          return `${row.webhookAccess ? 1 : 0}${row.emailAuth ? 1 : 0}`;
        case "renewal":
          return Number(row.daysLeft ?? 0);
        case "health":
          return row.health;
        default:
          return row.organizationName;
      }
    };

    const nextRows = [...filteredRows].sort((left, right) => {
      const comparison = compareValues(getSortValue(left), getSortValue(right));
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return nextRows;
  }, [filteredRows, sortDirection, sortKey]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortDirection("asc");
  };

  useEffect(() => {
    if (!filteredRows.length) {
      setSelectedEnterpriseId("");
      setDetail(null);
      setDetailOpen(false);
      setSelectedUserId("");
      return;
    }

    const stillVisible = filteredRows.some((row) => row.id === selectedEnterpriseId);
    if (!stillVisible) {
      setSelectedEnterpriseId("");
      setDetail(null);
      setDetailOpen(false);
      setSelectedUserId("");
    }
  }, [filteredRows, selectedEnterpriseId]);

  useEffect(() => {
    if (!selectedEnterpriseId) return;

    let cancelled = false;

    const loadDetail = async () => {
      try {
        setDetailLoading(true);
        const result = await getEnterpriseAccountDetailV2(selectedEnterpriseId);

        if (cancelled) return;

        setDetail(result);
        setSelectedUserId(result?.users?.[0]?.id || "");
      } catch (error) {
        if (!cancelled) {
          showToastMessage(error?.message || "Failed to load enterprise detail");
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();

    return () => {
      cancelled = true;
    };
  }, [selectedEnterpriseId]);

  const openEnterpriseDetail = (row) => {
    setSelectedEnterpriseId(row.id);
    setDetailOpen(true);
  };

  const summary = useMemo(() => {
    const expiringSoon = rows.filter(
      (row) => typeof row.daysLeft === "number" && row.daysLeft >= 0 && row.daysLeft <= 14
    ).length;
    const webhookEnabled = rows.filter((row) => row.webhookAccess).length;
    const lowCapacity = rows.filter((row) => Number(row.remainingCards || 0) <= 5).length;

    return [
      {
        label: "Enterprise workspaces",
        value: rows.length,
        note: "Migrating from legacy admin into Admin 2.0",
        icon: <Building2 size={18} />,
      },
      {
        label: "Expiring soon",
        value: expiringSoon,
        note: "Renewal due within the next 14 days",
        icon: <Sparkles size={18} />,
      },
      {
        label: "Webhook enabled",
        value: webhookEnabled,
        note: "Accounts with integration access available",
        icon: <ShieldCheck size={18} />,
      },
      {
        label: "Low capacity",
        value: lowCapacity,
        note: "5 or fewer cards remaining",
        icon: <Users size={18} />,
      },
    ];
  }, [rows]);

  const integrityMap = useMemo(() => {
    const map = new Map();
    (workspaceData?.integrity?.rows || []).forEach((item) => {
      if (item?.accountId) map.set(String(item.accountId), item);
    });
    return map;
  }, [workspaceData]);

  const openCreate = () => {
    setSelected(null);
    setSubmitError("");
    setSheetOpen(true);
  };

  const openEdit = (row) => {
    setSelected(row);
    setSubmitError("");
    setSheetOpen(true);
  };

  const setMailing = useCallback((type, userId, value) => {
    const key = `${type}:${userId}`;
    setMailingState((current) => ({
      ...current,
      [key]: value,
    }));
  }, []);

  const isMailing = useCallback(
    (type, userId) => Boolean(mailingState[`${type}:${userId}`]),
    [mailingState]
  );

  const handleWorkspaceEmail = useCallback(
    async (userId, type) => {
      if (!userId) return;

      try {
        setMailing(type, userId, true);

        if (type === "password") {
          const result = await resendWorkspaceCreatePasswordV2(userId);
          showToastMessage(result?.message || "Create password email sent successfully.");
          return;
        }

        const result = await resendWorkspaceWalletEmailV2(userId);
        showToastMessage(result?.message || "Add to wallet email sent successfully.");
      } catch (error) {
        showToastMessage(error?.message || "Unable to send workspace email.");
      } finally {
        setMailing(type, userId, false);
      }
    },
    [setMailing]
  );

  const handleCopyWorkspaceLink = useCallback(
    async (userId, type, userName) => {
      if (!userId) return;

      try {
        setMailing(type, userId, true);

        const payload =
          type === "copy-password"
            ? await generateWorkspaceCreatePasswordLinkV2(userId)
            : await generateWorkspaceWalletLinkV2(userId);

        const label =
          type === "copy-password"
            ? `${userName || payload?.name || "User"} - Create Password`
            : `${userName || payload?.name || "User"} - Add to Wallet`;

        await navigator.clipboard.writeText(payload?.link || "");
        showToastMessage(`${label} link copied successfully.`);
      } catch (error) {
        showToastMessage(error?.message || "Unable to copy workspace link.");
      } finally {
        setMailing(type, userId, false);
      }
    },
    [setMailing]
  );

  const handleSubmit = async (payload) => {
    try {
      setSubmitLoading(true);
      setSubmitError("");

      const result = selected?.id
        ? await updateEnterpriseAccountV2(selected.id, payload)
        : await createEnterpriseAccountV2(payload);

      setRows((current) => {
        if (selected?.id) {
          return current.map((row) => (row.id === selected.id ? result : row));
        }

        return [result, ...current];
      });

      setSelectedEnterpriseId(result.id);
      setSheetOpen(false);
      setSelected(null);
      showToastMessage(
        selected?.id
          ? "Enterprise account updated successfully"
          : "Enterprise account created successfully"
      );
    } catch (error) {
      setSubmitError(error?.message || "Unable to save enterprise account");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="tw-space-y-6">
      <section className="admin-v2-panel tw-p-5 sm:tw-p-6 lg:tw-p-8">
        <div className="tw-grid tw-gap-6 xl:tw-grid-cols-[minmax(0,1fr)_auto] xl:tw-items-end">
          <div className="tw-space-y-3">
            <p className="admin-v2-eyebrow">Enterprise Accounts</p>
            <h1 className="admin-v2-title">
              Manage Enterprises.
            </h1>
            <p className="admin-v2-subtitle">
              Click any enterprise row to inspect full workspace detail, users, package health, and renewal timing.
            </p>
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-3">
            <button
              type="button"
              className="admin-v2-action admin-v2-action-secondary"
              onClick={() => loadRows("refresh")}
            >
              <RefreshCw size={16} className={refreshing ? "tw-animate-spin" : ""} />
              Refresh
            </button>
            <button
              type="button"
              className="admin-v2-action admin-v2-action-primary"
              onClick={openCreate}
            >
              <Plus size={16} />
              Create enterprise
            </button>
          </div>
        </div>
      </section>

      <section className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
        <div className="tw-grid tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-4">
          {summary.map((item) => (
            <div key={item.label} className="admin-v2-stat-panel">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <p className="admin-v2-eyebrow">{item.label}</p>
                <span className="tw-text-[var(--admin-v2-accent)]">{item.icon}</span>
              </div>
              <p className="tw-mt-5 tw-text-4xl tw-font-semibold tw-tracking-tight tw-text-[var(--admin-v2-ink)]">
                {item.value}
              </p>
              <p className="tw-mt-2 tw-text-sm admin-v2-text-muted">{item.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="admin-v2-soft-panel tw-p-4 lg:tw-p-6">
          <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 sm:tw-flex-row">
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search workspace, owner, email, package..."
                className="tw-w-full tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)] focus:tw-border-[rgba(243,86,88,0.5)] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[rgba(243,86,88,0.08)] sm:tw-max-w-[420px]"
              />
              <select
                value={healthFilter}
                onChange={(event) => setHealthFilter(event.target.value)}
                className="tw-rounded-2xl tw-border tw-border-[rgba(31,41,64,0.12)] tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-[var(--admin-v2-ink)] focus:tw-border-[rgba(243,86,88,0.5)] focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-[rgba(243,86,88,0.08)]"
              >
                <option value="all">All health states</option>
                <option value="healthy">Healthy</option>
                <option value="expiring soon">Expiring soon</option>
                <option value="expired">Expired</option>
                <option value="no subscription">No subscription</option>
              </select>
            </div>

            <div className="admin-v2-brand-badge">
              <span className="admin-v2-brand-dot" />
              {sortedRows.length} enterprise records
            </div>
          </div>

          <div className="tw-mt-5">
            {loading ? (
              <div className="admin-v2-text-muted tw-p-8">Loading enterprise accounts...</div>
            ) : !sortedRows.length ? (
              <div className="admin-v2-text-muted tw-p-8">No enterprise accounts match the current filters.</div>
            ) : (
              <>
                <div className="admin-v2-table-desktop tw-overflow-x-auto">
                  <table className="admin-v2-data-table">
                    <thead>
                      <tr>
                        {[
                          ["workspace", "Workspace"],
                          ["owner", "Owner"],
                          ["package", "Package"],
                          ["cards", "Cards"],
                          ["security", "Security"],
                          ["renewal", "Renewal"],
                          ["health", "Health"],
                        ].map(([key, label]) => (
                          <th key={key}>
                            <button
                              type="button"
                              onClick={() => handleSort(key)}
                              className="tw-inline-flex tw-items-center tw-gap-2 tw-text-left tw-text-inherit"
                            >
                              <span>{label}</span>
                              <ArrowUpDown
                                size={13}
                                className={
                                  sortKey === key
                                    ? "tw-text-[var(--admin-v2-accent)]"
                                    : "tw-text-[var(--admin-v2-muted)]"
                                }
                              />
                            </button>
                          </th>
                        ))}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row) => {
                        const selectedRow = selectedEnterpriseId === row.id;
                        const integrity = integrityMap.get(String(row.id));

                        return (
                          <tr
                            key={row.id}
                            onClick={() => openEnterpriseDetail(row)}
                            className={`tw-cursor-pointer ${
                              selectedRow && detailOpen ? "tw-bg-[rgba(243,86,88,0.04)]" : ""
                            }`}
                          >
                            <td>
                              <div className="tw-space-y-1.5">
                                <p className="tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                                  {row.organizationName}
                                </p>
                                <div className="tw-flex tw-flex-wrap tw-gap-2">
                                  <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[rgba(44,50,85,0.05)] tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-medium tw-text-[var(--admin-v2-muted)]">
                                    {row.reportCount} reports
                                  </span>
                                  <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[rgba(250,216,69,0.14)] tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-medium tw-text-[var(--admin-v2-ink)]">
                                    {row.remainingCards} cards left
                                  </span>
                                  {(integrity?.issues || []).slice(0, 2).map((issue) => (
                                    <span
                                      key={`${row.id}-${issue}`}
                                      className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[rgba(243,86,88,0.10)] tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-medium tw-text-[#c44749]"
                                    >
                                      {issue}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="tw-space-y-1.5">
                                <p className="tw-font-medium tw-text-[var(--admin-v2-ink)]">
                                  {row.ownerName}
                                </p>
                                <p className="tw-text-sm admin-v2-text-muted">{row.email}</p>
                              </div>
                            </td>
                            <td>
                              <div className="tw-space-y-1.5">
                                <p className="tw-font-medium tw-text-[var(--admin-v2-ink)]">
                                  {row.packageName}
                                </p>
                                {(() => {
                                  const term = getTermDisplay(row);
                                  return (
                                    <>
                                      <p className="tw-text-sm admin-v2-text-muted">{term.label}</p>
                                      {term.hint ? (
                                        <p className="tw-mt-1 tw-max-w-[220px] tw-text-xs admin-v2-text-muted">
                                          {term.hint}
                                        </p>
                                      ) : null}
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                            <td>
                              <div className="tw-min-w-[180px] tw-space-y-2">
                                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-text-xs">
                                  <span className="admin-v2-text-muted">{row.usedCards} used</span>
                                  <span className="tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                                    {row.remainingCards} left
                                  </span>
                                </div>
                                <div className="admin-v2-progress">
                                  <span
                                    style={{
                                      width: `${
                                        row.totalCards > 0
                                          ? Math.min((row.usedCards / row.totalCards) * 100, 100)
                                          : 0
                                      }%`,
                                    }}
                                  />
                                </div>
                                <p className="tw-text-xs admin-v2-text-muted">
                                  {row.totalCards > 0 ? `${row.totalCards} total cards` : "No card limit found"}
                                </p>
                              </div>
                            </td>
                            <td>
                              <div className="tw-space-y-2">
                                <StatusBadge
                                  status={row.webhookAccess ? "Webhook enabled" : "Webhook off"}
                                />
                                <StatusBadge status={row.emailAuth ? "Email auth" : "SSO only"} />
                              </div>
                            </td>
                            <td>
                              <div className="tw-space-y-1">
                                <p className="tw-font-medium tw-text-[var(--admin-v2-ink)]">
                                  {formatDays(row.daysLeft)}
                                </p>
                                <p className="tw-text-sm admin-v2-text-muted">
                                  {formatDate(row.nextPaymentDate)}
                                </p>
                              </div>
                            </td>
                            <td>
                              <StatusBadge status={row.health} />
                            </td>
                            <td>
                              <EnterpriseRowActions row={row} onEdit={openEdit} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="admin-v2-data-grid md:tw-hidden">
                  {sortedRows.map((row) => {
                    const integrity = integrityMap.get(String(row.id));
                    return (
                    <article
                      key={row.id}
                      onClick={() => openEnterpriseDetail(row)}
                      className={`admin-v2-mobile-card tw-space-y-4 tw-cursor-pointer ${
                        selectedEnterpriseId === row.id && detailOpen
                          ? "tw-ring-2 tw-ring-[rgba(243,86,88,0.16)]"
                          : ""
                      }`}
                    >
                      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                        <div>
                          <p className="tw-text-base tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                            {row.organizationName}
                          </p>
                          <p className="tw-text-sm admin-v2-text-muted">{row.ownerName}</p>
                        </div>
                        <StatusBadge status={row.health} />
                      </div>

                      <p className="tw-text-sm admin-v2-text-muted">{row.email}</p>

                      <div className="admin-v2-progress">
                        <span
                          style={{
                            width: `${
                              row.totalCards > 0
                                ? Math.min((row.usedCards / row.totalCards) * 100, 100)
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                      <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                        <span className="admin-v2-text-muted">{row.usedCards} used</span>
                        <span className="tw-font-semibold tw-text-[var(--admin-v2-ink)]">
                          {row.remainingCards} left
                        </span>
                      </div>

                      {(integrity?.issues || []).length ? (
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                          {(integrity.issues || []).slice(0, 2).map((issue) => (
                            <span
                              key={`${row.id}-${issue}-mobile`}
                              className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[rgba(243,86,88,0.10)] tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-medium tw-text-[#c44749]"
                            >
                              {issue}
                            </span>
                          ))}
                        </div>
                      ) : null}

                      <EnterpriseRowActions row={row} onEdit={openEdit} />
                    </article>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

      </section>

      <EnterpriseDetailPanel
        open={detailOpen}
        detail={detail}
        loading={detailLoading}
        onClose={() => setDetailOpen(false)}
        selectedUserId={selectedUserId}
        onSelectUser={setSelectedUserId}
        onEditEnterprise={openEdit}
        onCreatePassword={(userId) => handleWorkspaceEmail(userId, "password")}
        onWalletEmail={(userId) => handleWorkspaceEmail(userId, "wallet")}
        onCopyCreatePassword={(userId, userName) => handleCopyWorkspaceLink(userId, "copy-password", userName)}
        onCopyWallet={(userId, userName) => handleCopyWorkspaceLink(userId, "copy-wallet", userName)}
        isMailing={isMailing}
      />

      <EnterpriseAccountFormSheet
        open={sheetOpen}
        account={selected}
        loading={submitLoading}
        error={submitError}
        onClose={() => {
          if (submitLoading) return;
          setSheetOpen(false);
          setSelected(null);
          setSubmitError("");
          if (location.search === "?create=1") {
            navigate("/admin-v2/enterprises", { replace: true });
          }
        }}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
